import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import dayjs from 'dayjs';
import classnames from 'classnames';
import style from './MeetingInfo.module.scss';
import { AttendState, MeetingConflict, MeetingRecord, Meeting, InventTag, AllMeetingStatus, NewOrderSignStatus } from 'type/meeting';
import { revokeMeeting, setMeetingInfo, cancelMeeting, closeMeeting, changeAttendState, cancelThisRepeatMeeting, cancelRepeatMeeting } from 'model/meeting';
import { useHistory } from 'react-router-dom';
import { Button, Modal, Popover, Dropdown, Menu, message } from 'antd';
import type { ModalFuncProps } from 'antd/lib/modal';
import ActionConfirmModal, { ActionConfirmModalOptions } from 'component/ActionConfirmModal';
import cooperationSDK from 'lib/cooperationSdk';
import { RedirectType } from 'util/meeting';
import { queryStringify, formatEndTime } from 'util/format';
import moment from 'moment';
import IotModal from '../IotModal';
import tracker from 'lib/tracker';
import { Meeting as MeetingApi } from 'api';
import APP_ROUTE from 'constant/route';
import {
  buildExtendMeetingRebookUrl,
  buildRebookMeetingUrl,
  getExtendMeetingErrorMessage,
  isExtendMeetingConflictError,
  promptRebookOnExtendMeetingError
} from 'util/meetingExtendRebook';
import { showExtendMeetingError } from 'util/showExtendMeetingError';

import { getStaffId } from 'data/storage';

import { signRoom } from 'model/room';
import UnderApprovalTag from 'component/RoomTable/components/UnderApprovalTag';
import Icon from 'component/Icon';
import { MeetingOptionType } from 'type/meetingDetail';
import ScheduleMeetingCard from './ScheduleMeetingCard';
import { ScheduleAction } from './schedulePresentation';

interface IProps {
  from?: string;
  record: MeetingRecord;
  refresh?: any;
}

type StateTag = {
  tag: string;
  clz: string;
};
const TO_BE_CONFIRMED = '新邀请';
const INVITE_WITHDRAW = '已取消';
const REFUSED = '已拒绝';
const ASK_FOR_LEAVE = '已请假';
const MEETING_EXTEND_MINUTES = 30;

const InvitedTagMap: { [k: number]: string } = {
  [AttendState.TO_BE_CONFIRMED]: TO_BE_CONFIRMED,
  [AttendState.INVITE_WITHDRAW]: INVITE_WITHDRAW
};

const AbnormalInviteMap = {
  [AttendState.REFUSED]: {
    label: REFUSED,
    buttonClz: 'refusedInvite',
    menus: [
      { label: '改为参加', state: AttendState.JOINED },
      { label: '改为请假', state: AttendState.ASK_FOR_LEAVE }
    ]
  },
  [AttendState.ASK_FOR_LEAVE]: {
    label: ASK_FOR_LEAVE,
    buttonClz: 'leaveInvite',
    menus: [
      { label: '改为参加', state: AttendState.JOINED },
      { label: '改为拒绝', state: AttendState.REFUSED }
    ]
  }
};

const isAbnormalInviteState = (state: AttendState) => state === AttendState.REFUSED || state === AttendState.ASK_FOR_LEAVE;

const MeetingInfo: React.FC<IProps> = (props) => {
  const { from, record, refresh } = props;
  const { meeting } = record;

  const history = useHistory();
  const dispatch = useDispatch();

  const [invitingTag, setInvitingTag] = useState('');
  const [isCreator, setIsCreator] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isExtending, setIsExtending] = useState(false);
  const [meetingStateTag, setMeetingStateTag] = useState(null as StateTag | null);
  const [currentInviteState, setCurrentInviteState] = useState(record.inviteState);
  const [invitePopoverVisible, setInvitePopoverVisible] = useState(false);

  const beginTime = dayjs(meeting.beginTime);
  const endTime = dayjs(meeting.endTime);
  const staffId = getStaffId();
  const isSchedulePage = from === 'schedule';
  const curTime = moment().valueOf();
  const isMeetingEnded = meeting.status === AllMeetingStatus.FINISH || meeting.status === AllMeetingStatus.FILED || curTime >= meeting?.endTime;

  const roomInfo = useMemo(() => {
    if (!meeting?.roomOrderList || meeting.roomOrderList?.length === 0) return '线上会议';
    if (meeting?.roomOrderList.length > 1) {
      return `${meeting.roomOrderList[0].address} ...共${meeting.roomOrderList.length}个地点`;
    } else {
      return meeting.roomOrderList[0]?.address;
    }
  }, [record]);

  useEffect(() => {
    if (!record || !record.meeting) return;
    const tag = isSchedulePage && isMeetingEnded && currentInviteState === AttendState.TO_BE_CONFIRMED ? '' : InvitedTagMap[currentInviteState];
    const isChange = record.changeLogs && record.changeLogs.length > 0;
    // 判断是否有纪要
    const hasSummary = record.summaryState === 1 ? '有纪要' : '';
    const disBand = meeting.status === AllMeetingStatus.CANCEL ? '已取消' : '';

    if (!disBand && isAbnormalInviteState(currentInviteState)) {
      setInvitingTag('');
      return;
    }

    setInvitingTag(disBand || hasSummary || tag || (isChange && '有变更') || '');
  }, [record, currentInviteState, meeting.status, isMeetingEnded, isSchedulePage]);

  useEffect(() => {
    setCurrentInviteState(record.inviteState);
    setInvitePopoverVisible(false);
  }, [record.inviteState, meeting.meetingNo]);

  useEffect(() => {
    const status =
      meeting.status === AllMeetingStatus.CANCEL
        ? null
        : meeting.status === AllMeetingStatus.FINISH
          ? { tag: '已结束', clz: style.disabled }
          : meeting.status === AllMeetingStatus.AUDIT_EXPIRED
            ? { tag: '已过期', clz: style.disabled }
            : meeting.status === AllMeetingStatus.FILED
              ? { tag: '已归档', clz: style.disabled }
              : meeting.status === AllMeetingStatus.AUDIT_REVOKE
                ? { tag: '已撤销', clz: style.disabled }
                : meeting.status === AllMeetingStatus.AUDIT_REFUSE
                  ? { tag: '审批拒绝', clz: style.disabled }
                  : meeting.status === AllMeetingStatus.DURING
                    ? { tag: '会议中', clz: style.primary }
                    : { tag: '待开始', clz: style.focus };

    setMeetingStateTag(status);
    setIsCreator(staffId === meeting.creatorStaffId);
  }, [meeting]);

  const meetingClz = classnames(
    style.meeting,
    meeting?.status === AllMeetingStatus.DURING
      ? style.begin
      : isMeetingEnded
        ? style.past
        : ''
  );
  const conflictMeetingClz = classnames(style.meta, style.conflict);

  const isConflict = (meeting.status === AllMeetingStatus.BEFORE || meeting.status === AllMeetingStatus.DURING) && record.conflict === MeetingConflict.YES;
  // 是否是审批会议
  const isApprovalMeeting = Boolean(meeting?.status === AllMeetingStatus.AUDITING);
  const abnormalInvite = AbnormalInviteMap[currentInviteState as AttendState.REFUSED | AttendState.ASK_FOR_LEAVE];
  const isShowAbnormalInvite = !isApprovalMeeting && meeting.status !== AllMeetingStatus.CANCEL && Boolean(abnormalInvite);
  const canChangeInviteState = !isCreator && (meeting.status === AllMeetingStatus.BEFORE || meeting.status === AllMeetingStatus.DURING);
  const isRefusedInvite = currentInviteState === AttendState.REFUSED;

  const checkout = () => {
    // FIX: 跳转前先清除旧数据，防止获取到错误的数据
    dispatch(setMeetingInfo({} as Meeting));

    const query: any = from ? { from } : {};
    if (record.inviteState === AttendState.INVITE_WITHDRAW) {
      query.type = RedirectType.WITHDRAW;
    }
    tracker?.reporteSchOperation('点击进入详情');
    redirect(`/details/${record.meeting.meetingNo}`, queryStringify(query));
  };
  const signMsgColor = meeting.status === AllMeetingStatus.BEFORE || AllMeetingStatus.DURING;
  const redirect = (path: string, query?: string) => {
    if (cooperationSDK.isCoEnv) {
      return cooperationSDK.createWindow({ url: location.origin + path, minWidth: 1050, minHeight: 700 });
    }

    history.push(`${path}${query || ''}`);
  };

  const closeIotMsg = () => {
    setIsModalVisible(false);
  };

  const confirmMeetingAction = (options: ModalFuncProps, scheduleOptions: Partial<ActionConfirmModalOptions> = {}) => {
    if (!isSchedulePage) return Modal.confirm(options);
    return ActionConfirmModal({
      title: options.title,
      content: options.content,
      onOk: options.onOk,
      onCancel: options.onCancel,
      ...scheduleOptions
    });
  };

  const handleClickCancel = (e: any, type: MeetingOptionType) => {
    e.stopPropagation();
    const cancelScope = {
      [MeetingOptionType.SingleMeeting]: { title: '取消会议', target: '该会议' },
      [MeetingOptionType.ThisMeeting]: { title: '取消本次会议', target: '本次会议' },
      [MeetingOptionType.PartMeeting]: { title: '取消本次及后续周期会议', target: '本次及后续周期会议' },
      [MeetingOptionType.AllMeeting]: { title: '取消所有周期会议', target: '所有周期会议' }
    }[type];
    confirmMeetingAction({
      title: '取消预约',
      icon: '',
      content: '取消后将通知各个参会人，且无法恢复，确定要取消吗？',
      okText: '取消预约',
      cancelText: '保留会议',
      onOk: () => _cancelMeeting(type),
      onCancel: () => {
        tracker?.reporteSchOperation('保留会议');
      },
      okType: 'danger',
      centered: true,
      autoFocusButton: null
    }, {
      title: cancelScope.title,
      content: `取消后，${cancelScope.target}将被撤销，参会人将收到取消通知，是否确认取消？`
    });
  };

  const handleClickCloseMeeting = (e: { stopPropagation: () => void }) => {
    e.stopPropagation();
    confirmMeetingAction({
      title: '提前结束',
      icon: '',
      content: '确定要提前结束会议吗？',
      okText: '提前结束',
      cancelText: '取消',
      onOk: _closeMeeting,
      centered: true,
      autoFocusButton: null
    }, { content: '确定要提前结束当前正在进行的会议吗？' });
  };

  const handleClickExtendMeeting = (e: { stopPropagation: () => void }) => {
    e.stopPropagation();
    if (isExtending) return;

    confirmMeetingAction({
      title: '延长会议',
      icon: '',
      content: `确定要将会议延长${MEETING_EXTEND_MINUTES}分钟吗？`,
      okText: '确认延长',
      cancelText: '取消',
      onOk: _extendMeeting,
      centered: true,
      autoFocusButton: null
    });
  };

  const handleClickRevokeMeeting = (e: { stopPropagation: () => void }) => {
    e.stopPropagation();
    confirmMeetingAction({
      title: '撤销申请',
      icon: '',
      content: '撤销申请后无法恢复，确定要撤销吗？',
      okText: '确定',
      cancelText: '取消',
      onOk: _revokeMeeting,
      centered: true,
      autoFocusButton: null
    });
  };

  const handleClickRebookMeeting = (e: { stopPropagation: () => void }) => {
    e.stopPropagation();
    tracker?.reporteSchOperation('再次预约');
    redirect(buildRebookMeetingUrl(`${APP_ROUTE.ORDER_MEETING}${queryStringify(from ? { from } : {})}`, meeting.meetingNo));
  };

  const handleClickSummary = async () => {
    tracker?.reporteSchOperation('查看纪要');
    const summaryModal = Modal.info({
      title: '会议纪要',
      icon: null,
      content: '正在加载纪要...',
      okText: '关闭',
      width: 640,
      centered: true,
      maskClosable: true
    });
    try {
      const summary = await MeetingApi.getSummary(meeting.meetingNo).toPromise();
      summaryModal.update({
        content: (
          <div>
            <div style={{ marginBottom: 16, fontWeight: 500 }}>{meeting.subject}</div>
            <div style={{ maxHeight: '50vh', overflowY: 'auto', whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
              {summary?.content || '暂无会议纪要'}
            </div>
          </div>
        )
      });
    } catch {
      summaryModal.update({ content: '纪要加载失败，请关闭后重试' });
    }
  };

  const _cancelMeeting = (type: MeetingOptionType) => {
    let time = 1000;
    tracker?.reporteSchOperation('取消会议预约');
    switch (type) {
      case MeetingOptionType.ThisMeeting:
        dispatch(cancelThisRepeatMeeting(meeting.meetingNo));
        break;
      case MeetingOptionType.AllMeeting:
        time = 2500;
        dispatch(cancelRepeatMeeting({ repeatMeetingNo: meeting.repeatMeetingNo }));
        break;
      case MeetingOptionType.PartMeeting:
        time = 2500;
        dispatch(cancelRepeatMeeting({ repeatMeetingNo: meeting.repeatMeetingNo, startTimeGe: meeting.beginTime }));
        break;
      case MeetingOptionType.SingleMeeting:
        dispatch(cancelMeeting(meeting));
        break;
    }
    setTimeout(() => {
      refresh();
    }, time);
  };

  const _closeMeeting = () => {
    tracker?.reporteSchOperation('提前结束');
    dispatch(closeMeeting(meeting));
    setTimeout(() => {
      refresh();
    }, 1000);
  };

  const handleExtendMeetingError = (error: any) => {
    const rebook = () => redirect(buildExtendMeetingRebookUrl(APP_ROUTE.ORDER_MEETING, meeting.meetingNo));
    if (isSchedulePage) {
      showExtendMeetingError(error, rebook);
      return;
    }
    if (!isExtendMeetingConflictError(error)) {
      message.error(getExtendMeetingErrorMessage(error));
    }
    promptRebookOnExtendMeetingError(error, rebook);
  };

  const _extendMeeting = async () => {
    if (!meeting.meetingNo || !meeting.endTime) return;

    setIsExtending(true);
    try {
      const response = await MeetingApi.extendMeeting(meeting.meetingNo, {
        newEndTime: meeting.endTime + MEETING_EXTEND_MINUTES * 60 * 1000
      }).toPromise();
      const isSuccess = response?.success !== false && (response?.code === undefined || response?.code === 0 || response?.code === 200);

      if (!isSuccess) {
        handleExtendMeetingError(response);
        return;
      }

      tracker?.reporteSchOperation('延长会议');
      message.success(`会议已延长${MEETING_EXTEND_MINUTES}分钟`);
      refresh?.();
    } catch (error) {
      handleExtendMeetingError(error);
    } finally {
      setIsExtending(false);
    }
  };
  const _revokeMeeting = () => {
    if (!meeting.meetingNo) return;
    tracker?.reporteSchOperation('撤销申请');
    dispatch(revokeMeeting(meeting.meetingNo));
    setTimeout(() => {
      refresh();
    }, 1000);
  };

  const signClick = (e: { stopPropagation: () => void }) => {
    e.stopPropagation();
    if (meeting.status === AllMeetingStatus.BEFORE || meeting.status === AllMeetingStatus.DURING) {
      // setIsModalVisible(true);
    }
    tracker?.reporteSchOperation('会议室签到');
    dispatch(signRoom(meeting.meetingNo));
    setTimeout(() => {
      refresh();
    }, 1000);
  };

  const iconPointStyle = (target: string) => {
    if (target === TO_BE_CONFIRMED) {
      return '#ff6b00';
    } else if (target === INVITE_WITHDRAW) {
      return '#FF0000';
    }
    return '#ff6b00';
  };

  const isShowSingned = () => {
    if (
      invitingTag === TO_BE_CONFIRMED ||
      invitingTag === INVITE_WITHDRAW ||
      currentInviteState === AttendState.REFUSED ||
      currentInviteState === AttendState.ASK_FOR_LEAVE
    ) {
      return false;
    }
    return meeting?.meetingSignView?.signStatus === NewOrderSignStatus.FINISH_SIGN;
  };

  const isShowMeetingStateTag = () => {
    return isApprovalMeeting ? false : meetingStateTag;
  };

  const isShowCancelMeeting = () => {
    if (isApprovalMeeting) {
      return false;
    }
    return isCreator && meeting.status === AllMeetingStatus.BEFORE;
  };

  const showRefuseConfirm = (onOk: () => void) => {
    setInvitePopoverVisible(false);
    confirmMeetingAction({
      title: '确定不参加该场会议吗？',
      icon: '',
      okText: '不参加',
      cancelText: '取消',
      onOk,
      centered: true,
      autoFocusButton: null
    }, { title: '拒绝会议', content: '确定拒绝该会议邀请？' });
  };

  const chgAttenStatus = (toState: AttendState) => {
    setInvitePopoverVisible(false);
    setCurrentInviteState(toState);
    dispatch(
      changeAttendState({
        meetingId: meeting.meetingNo,
        state: toState
      })
    );
    setTimeout(() => {
      refresh();
    }, 1000);
  };

  const handleChangeAttendStatus = (toState: AttendState) => {
    if (toState === AttendState.REFUSED) {
      showRefuseConfirm(() => chgAttenStatus(toState));
      return;
    }

    if (isSchedulePage && toState === AttendState.ASK_FOR_LEAVE) {
      ActionConfirmModal({
        title: '会议请假',
        content: '确定该会议请假吗？',
        onOk: () => chgAttenStatus(toState)
      });
      return;
    }

    chgAttenStatus(toState);
  };

  const handleClickInvents = (e: any, inventTarget: InventTag) => {
    e.stopPropagation();
    switch (inventTarget) {
      case InventTag.join:
        handleChangeAttendStatus(AttendState.JOINED);
        break;
      case InventTag.leave:
        handleChangeAttendStatus(AttendState.ASK_FOR_LEAVE);
        break;
      case InventTag.reject:
        handleChangeAttendStatus(AttendState.REFUSED);
        break;
      default:
        break;
    }
  };

  const getInvents = () => (
    <div className={style.invents}>
      <div
        onClick={(e) => {
          handleClickInvents(e, InventTag.join);
        }}
        className={style.invent_item}
      >
        参加
      </div>
      <div
        onClick={(e) => {
          handleClickInvents(e, InventTag.leave);
        }}
        className={style.invent_item}
      >
        请假
      </div>
      <div
        onClick={(e) => {
          handleClickInvents(e, InventTag.reject);
        }}
        className={style.invent_item}
      >
        拒绝
      </div>
    </div>
  );

  const getAbnormalInvents = () => (
    <div className={style.invents}>
      {abnormalInvite?.menus.map((item) => (
        <div
          key={item.label}
          onClick={(e) => {
            e.stopPropagation();
            handleChangeAttendStatus(item.state);
          }}
          className={style.invent_item}
        >
          {item.label}
        </div>
      ))}
    </div>
  );

  const getAbnormalInviteButton = () => (
    <div
      className={classnames(style.abnormalInvite, style[abnormalInvite?.buttonClz || ''])}
      onClick={(e) => {
        e.stopPropagation();
      }}
    >
      <div>{abnormalInvite?.label}</div>
      {canChangeInviteState && <Icon size={16} color={currentInviteState === AttendState.ASK_FOR_LEAVE ? '#FA8C16' : '#999999'} type="iconinput_arrow_down_ic1x" />}
    </div>
  );

  const menu = (
    <Menu>
      <Menu.Item onClick={(e) => handleClickCancel(e.domEvent, MeetingOptionType.ThisMeeting)}>取消本次会议</Menu.Item>
      <Menu.Item onClick={(e) => handleClickCancel(e.domEvent, MeetingOptionType.PartMeeting)}>取消本次及后续周期会议</Menu.Item>
      <Menu.Item onClick={(e) => handleClickCancel(e.domEvent, MeetingOptionType.AllMeeting)}>取消所有周期会议</Menu.Item>
    </Menu>
  );

  const handleScheduleAction = (action: ScheduleAction, event: { stopPropagation: () => void }) => {
    event.stopPropagation();
    switch (action) {
      case 'join':
        handleChangeAttendStatus(AttendState.JOINED);
        break;
      case 'leave':
        handleChangeAttendStatus(AttendState.ASK_FOR_LEAVE);
        break;
      case 'refuse':
        handleChangeAttendStatus(AttendState.REFUSED);
        break;
      case 'signIn':
        signClick(event);
        break;
      case 'extend':
        handleClickExtendMeeting(event);
        break;
      case 'end':
        handleClickCloseMeeting(event);
        break;
      case 'cancel':
        handleClickCancel(event, MeetingOptionType.SingleMeeting);
        break;
      case 'revoke':
        handleClickRevokeMeeting(event);
        break;
      case 'rebook':
        handleClickRebookMeeting(event);
        break;
      case 'summary':
        handleClickSummary();
        break;
    }
  };

  if (isSchedulePage) {
    return (
      <ScheduleMeetingCard
        record={{ ...record, inviteState: currentInviteState }}
        isCreator={staffId === meeting.creatorStaffId}
        isExtending={isExtending}
        roomInfo={roomInfo}
        onCheckout={checkout}
        onAction={handleScheduleAction}
        onCancel={handleClickCancel}
      />
    );
  }

  return (
    meeting && (
      <div className={meetingClz}>
        <div className={style.timeWarp}>
          <div className={style.time}>
            {beginTime.format('HH:mm')}
            <span className={style.dot}></span>
          </div>
        </div>
        <div className={style.infoWarp} onClick={checkout}>
          <div className={style.titleWarp}>
            <div className={classnames(style.title, isRefusedInvite && style.refusedTitle)}>{meeting.subject}</div>
            {isApprovalMeeting && <UnderApprovalTag className={style.margin_right} />}
            {isShowMeetingStateTag() && <div className={classnames(style.tag, meetingStateTag?.clz)}>{meetingStateTag?.tag}</div>}
            <div className={style.rightPane}>
              <div className={style.buttons}>
                {isSchedulePage && (
                  <Button className={style.rebookButton} onClick={handleClickRebookMeeting}>
                    再次预约
                  </Button>
                )}
                {isApprovalMeeting && (
                  <Button className={style.frontOverButton} onClick={handleClickRevokeMeeting}>
                    撤销申请
                  </Button>
                )}
                {isShowCancelMeeting() &&
                  (meeting.isRepeatMeeting ? (
                    <Dropdown overlay={menu} placement="bottomCenter">
                      <Button
                        className={style.cancelButton}
                        onClick={(e) => {
                          e.stopPropagation();
                        }}
                      >
                        取消预约
                      </Button>
                    </Dropdown>
                  ) : (
                    <Button className={style.cancelButton} onClick={(e) => handleClickCancel(e, MeetingOptionType.SingleMeeting)}>
                      取消预约
                    </Button>
                  ))}

                {isCreator && meeting.status === AllMeetingStatus.DURING && (
                  <Button id="extend-btn" className={style.extendButton} loading={isExtending} onClick={handleClickExtendMeeting}>
                    延长会议
                  </Button>
                )}
                {isCreator && meeting.status === AllMeetingStatus.DURING && (
                  <Button className={style.frontOverButton} onClick={handleClickCloseMeeting}>
                    提前结束
                  </Button>
                )}
                {!isApprovalMeeting && (
                  <>
                    {/* 如果没有接受邀请，则不显示 */}
                    {!isShowAbnormalInvite && invitingTag !== TO_BE_CONFIRMED && meeting?.meetingSignView?.isCanSign && (
                      <Button type="primary" onClick={signClick} className={style.sign}>
                        会议室签到
                      </Button>
                    )}
                  </>
                )}
                <div className={style.invitedTag} style={{ color: iconPointStyle(invitingTag) }}>
                  {invitingTag === TO_BE_CONFIRMED && (
                    <>
                      {meeting.status === AllMeetingStatus.FINISH ? (
                        <>
                          <div className={style.icon_point} style={{ background: iconPointStyle(invitingTag) }}></div>
                          <div className={style.text_color} style={{ color: iconPointStyle(invitingTag) }}>
                            {invitingTag}
                          </div>
                        </>
                      ) : (
                        <Popover content={getInvents()} placement="bottom" trigger="click" visible={invitePopoverVisible} onVisibleChange={setInvitePopoverVisible}>
                          <div
                            className={style.newInvite}
                            onClick={(e) => {
                              e.stopPropagation();
                            }}
                          >
                            <div>{TO_BE_CONFIRMED}</div>
                            <Icon size={16} color="#fff" type="iconinput_arrow_down_ic1x" />
                          </div>
                        </Popover>
                      )}
                    </>
                  )}
                  {invitingTag === INVITE_WITHDRAW && (
                    <>
                      <div className={style.icon_point} style={{ background: iconPointStyle(invitingTag) }}></div>
                      {invitingTag}
                    </>
                  )}
                  {isShowAbnormalInvite &&
                    (canChangeInviteState ? (
                      <Popover content={getAbnormalInvents()} placement="bottom" trigger="click" visible={invitePopoverVisible} onVisibleChange={setInvitePopoverVisible}>
                        {getAbnormalInviteButton()}
                      </Popover>
                    ) : (
                      getAbnormalInviteButton()
                    ))}
                </div>
              </div>
            </div>
          </div>
          <div className={classnames(isConflict ? conflictMeetingClz : style.meta, isRefusedInvite && style.refusedMeta)}>
            {beginTime.format('HH:mm')} - {formatEndTime(endTime.format('HH:mm'))}
            {isConflict && <span>（冲突）</span>}
          </div>
          <div className={classnames(style.meta, isRefusedInvite && style.refusedMeta)}>
            {roomInfo}
            {isShowSingned() && <span className={style.signed}>会议室已签到</span>}
          </div>
        </div>
        {signMsgColor && <IotModal show={isModalVisible} onClose={closeIotMsg} iotMessage={meeting.iotMessage} />}
      </div>
    )
  );
};

export default MeetingInfo;
